/**
 * Ergonomic Workstation Service
 * API do posto de trabalho avaliado da AET (#356): posto, atividades, cargas, equipamentos, danos, ações e fotos.
 */
import apiClient from './apiClient';
import type {
  ErgonomicAction,
  ErgonomicActionInput,
  ErgonomicActivity,
  ErgonomicActivityInput,
  ErgonomicComfort,
  ErgonomicDamage,
  ErgonomicDepartmentContext,
  ErgonomicDamageInput,
  ErgonomicEquipment,
  ErgonomicEquipmentInput,
  ErgonomicLoad,
  ErgonomicLoadInput,
  ErgonomicOptions,
  ErgonomicPhoto,
  ErgonomicPhotoKind,
  ErgonomicPhotoUpdateInput,
  ErgonomicWorkstationDependencies,
  ErgonomicWorkstationDetail,
  ErgonomicWorkstationInput,
  ErgonomicWorkstationListFilters,
  ErgonomicWorkstationListResponse,
  ErgonomicWorkstationSummary,
} from '../types/ergonomicWorkstation';

export * from '../types/ergonomicWorkstation';

type Id = string | number;

export interface ErgonomicWorkstationListParams extends ErgonomicWorkstationListFilters {
  page?: number;
  limit?: number;
}

const FILTROS_DA_QUERY = ['search', 'departmentId', 'status', 'situation', 'semFoto', 'companyId'] as const;

function montarParams(filtros: ErgonomicWorkstationListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  FILTROS_DA_QUERY.forEach((nome) => {
    const valor = filtros[nome];
    if (valor !== undefined && valor !== '' && valor !== false) params[nome] = String(valor);
  });
  return params;
}

class ErgonomicWorkstationService {
  private baseUrl = '/ergonomic-workstations';

  async list(params: ErgonomicWorkstationListParams = {}): Promise<ErgonomicWorkstationListResponse> {
    const { page = 1, limit = 20, ...filtros } = params;
    const response = await apiClient.get(this.baseUrl, {
      params: { ...montarParams(filtros), page: String(page), limit: String(limit) },
    });
    const data = response.data.data || response.data;
    return {
      workstations: data.workstations ?? [],
      total: data.total ?? 0,
      page: data.page || page,
      limit: data.limit || limit,
      totalPages: data.totalPages || Math.ceil((data.total ?? 0) / limit),
    };
  }

  /** Conta a lista inteira (não a página): alimenta as pílulas "precisam de atenção". */
  async summary(filtros: ErgonomicWorkstationListFilters = {}): Promise<ErgonomicWorkstationSummary> {
    const response = await apiClient.get(`${this.baseUrl}/summary`, { params: montarParams(filtros) });
    return response.data.data || response.data;
  }

  async options(): Promise<ErgonomicOptions> {
    const response = await apiClient.get(`${this.baseUrl}/options`);
    return response.data.data || response.data;
  }

  async getById(id: Id): Promise<ErgonomicWorkstationDetail> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  async create(data: ErgonomicWorkstationInput): Promise<ErgonomicWorkstationDetail> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  async update(id: Id, data: Partial<ErgonomicWorkstationInput>): Promise<ErgonomicWorkstationDetail> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  async remove(id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async dependencies(id: Id): Promise<ErgonomicWorkstationDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  /** Medição de conforto do setor do posto: lida, nunca digitada (spec D3). */
  async comfort(id: Id): Promise<ErgonomicComfort> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/comfort`);
    return response.data.data || response.data;
  }

  /** Contexto do setor escolhido, antes de o posto existir. `companyId` quando a empresa vem do formulário. */
  async departmentContext(departmentId: Id, companyId?: Id): Promise<ErgonomicDepartmentContext> {
    const response = await apiClient.get(`${this.baseUrl}/departments/${departmentId}/context`, {
      params: companyId !== undefined && companyId !== '' ? { companyId: String(companyId) } : undefined,
    });
    return response.data.data || response.data;
  }

  async getActivities(workstationId: Id): Promise<ErgonomicActivity[]> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/activities`);
    return response.data.data || response.data;
  }

  async createActivity(workstationId: Id, data: ErgonomicActivityInput): Promise<ErgonomicActivity> {
    const response = await apiClient.post(`${this.baseUrl}/${workstationId}/activities`, data);
    return response.data.data || response.data;
  }

  async updateActivity(workstationId: Id, id: Id, data: Partial<ErgonomicActivityInput>): Promise<ErgonomicActivity> {
    const response = await apiClient.put(`${this.baseUrl}/${workstationId}/activities/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteActivity(workstationId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${workstationId}/activities/${id}`);
  }

  async getLoads(workstationId: Id): Promise<ErgonomicLoad[]> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/loads`);
    return response.data.data || response.data;
  }

  async createLoad(workstationId: Id, data: ErgonomicLoadInput): Promise<ErgonomicLoad> {
    const response = await apiClient.post(`${this.baseUrl}/${workstationId}/loads`, data);
    return response.data.data || response.data;
  }

  async updateLoad(workstationId: Id, id: Id, data: Partial<ErgonomicLoadInput>): Promise<ErgonomicLoad> {
    const response = await apiClient.put(`${this.baseUrl}/${workstationId}/loads/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteLoad(workstationId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${workstationId}/loads/${id}`);
  }

  async getEquipments(workstationId: Id): Promise<ErgonomicEquipment[]> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/equipments`);
    return response.data.data || response.data;
  }

  async createEquipment(workstationId: Id, data: ErgonomicEquipmentInput): Promise<ErgonomicEquipment> {
    const response = await apiClient.post(`${this.baseUrl}/${workstationId}/equipments`, data);
    return response.data.data || response.data;
  }

  async updateEquipment(workstationId: Id, id: Id, data: Partial<ErgonomicEquipmentInput>): Promise<ErgonomicEquipment> {
    const response = await apiClient.put(`${this.baseUrl}/${workstationId}/equipments/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteEquipment(workstationId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${workstationId}/equipments/${id}`);
  }

  async getDamages(workstationId: Id): Promise<ErgonomicDamage[]> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/damages`);
    return response.data.data || response.data;
  }

  async createDamage(workstationId: Id, data: ErgonomicDamageInput): Promise<ErgonomicDamage> {
    const response = await apiClient.post(`${this.baseUrl}/${workstationId}/damages`, data);
    return response.data.data || response.data;
  }

  async updateDamage(workstationId: Id, id: Id, data: Partial<ErgonomicDamageInput>): Promise<ErgonomicDamage> {
    const response = await apiClient.put(`${this.baseUrl}/${workstationId}/damages/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteDamage(workstationId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${workstationId}/damages/${id}`);
  }

  async getActions(workstationId: Id): Promise<ErgonomicAction[]> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/actions`);
    return response.data.data || response.data;
  }

  async createAction(workstationId: Id, data: ErgonomicActionInput): Promise<ErgonomicAction> {
    const response = await apiClient.post(`${this.baseUrl}/${workstationId}/actions`, data);
    return response.data.data || response.data;
  }

  async updateAction(workstationId: Id, id: Id, data: Partial<ErgonomicActionInput>): Promise<ErgonomicAction> {
    const response = await apiClient.put(`${this.baseUrl}/${workstationId}/actions/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteAction(workstationId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${workstationId}/actions/${id}`);
  }

  async getPhotos(workstationId: Id): Promise<ErgonomicPhoto[]> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/photos`);
    return response.data.data || response.data;
  }

  /** Uma foto por requisição; o tipo vai na URL (`/photos/abertura` ou `/photos/registro`) e o arquivo no campo `file`. */
  async uploadPhoto(workstationId: Id, kind: ErgonomicPhotoKind, file: File): Promise<ErgonomicPhoto> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `${this.baseUrl}/${workstationId}/photos/${kind === 'ABERTURA' ? 'abertura' : 'registro'}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data || response.data;
  }

  async updatePhoto(workstationId: Id, id: Id, data: ErgonomicPhotoUpdateInput): Promise<ErgonomicPhoto> {
    const response = await apiClient.put(`${this.baseUrl}/${workstationId}/photos/${id}`, data);
    return response.data.data || response.data;
  }

  async deletePhoto(workstationId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${workstationId}/photos/${id}`);
  }

  async downloadPhoto(workstationId: Id, id: Id): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${workstationId}/photos/${id}/file`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }
}

export const ergonomicWorkstationService = new ErgonomicWorkstationService();
